import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/shared/components/forms/FileUpload';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { API_CONFIG } from '@/config';

interface AddInfoRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    fullName: string;
    description: string;
    phoneNumber: string;
    photoUrl?: string;
    resumeUrl?: string;
    isResumePublic: boolean;
    forSelf: boolean;
  }) => void;
}

export function AddInfoRequestDialog({
  open,
  onClose,
  onSubmit,
}: AddInfoRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    description: '',
    phoneNumber:'',
    photoUrl: '',
    resumeUrl: '',
    isResumePublic: false,
    forSelf: true,
  });

  const handleSubmit = async () => {
    if (!formData.fullName.trim()) {
      setError('الاسم مطلوب');
      return;
    }


    if (!formData.description.trim()) {
      setError('الوصف مطلوب');
      return;
    }

    if (!/^(009665\d{8}|00965[569]\d{7})$/.test(formData.phoneNumber.trim())) {
      setError('رقم الهاتف غير صحيح. يجب أن يكون بالصيغة الدولية:\n'+'\n السعودية: 009665xxxxxxx\n - الكويت: 00965xxxxxxx');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onSubmit(formData);
      setFormData({
        fullName: '',
        description: '',
        photoUrl: '',
        phoneNumber: '',
        resumeUrl: '',
        isResumePublic: false,
        forSelf: true,
      });
      onClose();
    } catch (error) {
      setError('حدث خطأ أثناء إرسال الطلب');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>طلب إضافة من أنت</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 px-[1rem]">
          <div className="space-y-6">
            <div className="space-y-4">
              <Label>نوع الطلب</Label>
              <RadioGroup
                value={formData.forSelf ? 'self' : 'other'}
                onValueChange={(value) => 
                  setFormData(prev => ({ ...prev, forSelf: value === 'self' }))
                }
                className="flex flex-col gap-2"
              >
                <div className="flex items-center justify-end gap-2">
                  <Label htmlFor="self" className="cursor-pointer">
                    أقوم بتعبئة معلوماتي الشخصية
                  </Label>
                  <RadioGroupItem value="self" id="self" />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Label htmlFor="other" className="cursor-pointer">
                    أقوم بتعبئة معلومات شخص آخر
                  </Label>
                  <RadioGroupItem value="other" id="other" />
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">الاسم الرباعي</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="أدخل الاسم الرباعي"
              />
            </div>


            <div className="space-y-2">
              <Label htmlFor="phoneNumber">رقم الجوال *</Label>
              <Input
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="رقم الجوال *"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">نبذة مختصرة</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="اكتب نبذة مختصرة"
              />
            </div>

            <div className="space-y-2">
              <Label>الصورة الشخصية (اختياري)</Label>
              <FileUpload
                type="image"
                value={formData.photoUrl ? API_CONFIG.BASE_URL + '/' +  formData.photoUrl : ""}
                onChange={(value) => setFormData(prev => ({ ...prev, photoUrl: value }))}
                label="الصورة الشخصية"
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>السيرة الذاتية PDF (اختياري)</Label>
                <FileUpload
                  type="pdf"
                  value={formData.resumeUrl ? API_CONFIG.BASE_URL + '/' +  formData.resumeUrl : ""}
                  onChange={(value) => setFormData(prev => ({ ...prev, resumeUrl: value }))}
                  label="ملف السيرة الذاتية"
                />
              </div>

              {formData.resumeUrl && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <Switch
                    checked={formData.isResumePublic}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, isResumePublic: checked }))
                    }
                  />
                  <Label className="text-sm text-gray-700">
                    جعل السيرة الذاتية متاحة للجميع
                  </Label>
                </div>
              )}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-500 mt-4 text-center">{error}</p>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.fullName.trim() || !formData.description.trim()}
            className="hover:bg-secondary"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner className="ml-2" />
                جاري الإرسال...
              </>
            ) : (
              'إرسال الطلب'
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
            className="mr-2"
          >
            إلغاء
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}